'use no memo';

import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import {useGetMagicPublishingRequestQuery} from '@dms/api/settings';
import {notificationTypes} from '@dms/shared';

import {AdvertisementPollingContextProvider} from '../contexts/AdvertisementPollingContext';

const POLLING_CONFIG = {
  // Time thresholds in minutes (how many minutes after the polling started the interval changes)
  THRESHOLDS: {
    MEDIUM: 15,
    SLOW: 30,
    STOP: 120, // Stop polling
  },
  // Polling intervals in milliseconds
  INTERVALS: {
    INITIAL: 2000,
    MEDIUM: 10000,
    SLOW: 30000,
  },
};

const MAGIC_PUBLISHING_POLLING_INTERVAL = 3000;

export const AdvertisementPollingContext = ({children}: {children: React.ReactNode}) => {
  const dispatch = useDispatch();

  // eslint-disable-next-line react-hooks/purity
  const pollingStartTimeRef = useRef<number>(Date.now());
  const [currentPollingInterval, setCurrentPollingInterval] = useState<number>(
    POLLING_CONFIG.INTERVALS.INITIAL
  );
  const [activeMagicRequestId, setActiveMagicRequestId] = useState<string | null>(null);
  const isMagicPublishingTerminalRef = useRef(false);

  const getPollingInterval = (elapsedTime: number): number => {
    const minutesToMs = (minutes: number) => minutes * 60 * 1000;

    if (elapsedTime >= minutesToMs(POLLING_CONFIG.THRESHOLDS.STOP)) {
      return 0;
    } else if (elapsedTime >= minutesToMs(POLLING_CONFIG.THRESHOLDS.SLOW)) {
      return POLLING_CONFIG.INTERVALS.SLOW;
    } else if (elapsedTime >= minutesToMs(POLLING_CONFIG.THRESHOLDS.MEDIUM)) {
      return POLLING_CONFIG.INTERVALS.MEDIUM;
    }
    return POLLING_CONFIG.INTERVALS.INITIAL;
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      const elapsedTime = Date.now() - pollingStartTimeRef.current;
      const newInterval = getPollingInterval(elapsedTime);

      if (newInterval !== currentPollingInterval) {
        setCurrentPollingInterval(newInterval);
      }
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [currentPollingInterval]);

  const resetPollingInterval = useCallback(() => {
    pollingStartTimeRef.current = Date.now();
    setCurrentPollingInterval(POLLING_CONFIG.INTERVALS.INITIAL);
  }, []);

  const setActiveMagicRequestIdHandler = useCallback((newId: string | null) => {
    isMagicPublishingTerminalRef.current = false;
    setActiveMagicRequestId(newId);
  }, []);

  const {data: magicPublishingRequest} = useGetMagicPublishingRequestQuery(
    {requestId: activeMagicRequestId ?? ''},
    {
      skip: activeMagicRequestId === null,
      //eslint-disable-next-line react-hooks/refs
      pollingInterval: isMagicPublishingTerminalRef.current ? 0 : MAGIC_PUBLISHING_POLLING_INTERVAL,
    }
  );

  //eslint-disable-next-line react-hooks/refs
  isMagicPublishingTerminalRef.current =
    magicPublishingRequest?.state === 'finished' || magicPublishingRequest?.state === 'failed';

  const isMagicPublishingActive = magicPublishingRequest?.state === 'started';

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === notificationTypes.MAGIC_PUBLISHING_FINISHED) {
      dispatch(
        omneticApi.util.invalidateTags([
          'AdGeneralSettings',
          {
            type: 'promotionalPhotoVehicle',
            id: notification.payload.saleVehicle.vehicleId,
          },
          'SaleVehicleAlbums',
        ])
      );
    }
  });

  return (
    <AdvertisementPollingContextProvider
      value={{
        currentPollingInterval,
        resetPollingInterval,
        isMagicPublishingActive,
        setActiveMagicRequestId: setActiveMagicRequestIdHandler,
      }}
    >
      {children}
    </AdvertisementPollingContextProvider>
  );
};
